import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { X, Shield, Cookie, Eye, Settings, CheckCircle } from 'lucide-react'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'

const GDPRBanner = () => {
  const [showBanner, setShowBanner] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [consent, setConsent] = useState(null)

  useEffect(() => {
    // Vérifier si le consentement a déjà été donné
    const existingConsent = localStorage.getItem('gdpr_consent')
    if (!existingConsent) {
      setShowBanner(true)
    } else {
      setConsent(JSON.parse(existingConsent))
    }
  }, [])

  const handleAcceptAll = () => {
    const newConsent = {
      timestamp: new Date().toISOString(),
      version: '1.0',
      cookies: true,
      analytics: true,
      marketing: true,
      preferences: true
    }
    
    localStorage.setItem('gdpr_consent', JSON.stringify(newConsent))
    setConsent(newConsent)
    setShowBanner(false)
  }

  const handleAcceptEssential = () => {
    const newConsent = {
      timestamp: new Date().toISOString(),
      version: '1.0',
      cookies: true,
      analytics: false,
      marketing: false,
      preferences: false
    }
    
    localStorage.setItem('gdpr_consent', JSON.stringify(newConsent))
    setConsent(newConsent)
    setShowBanner(false)
  }

  const handleDecline = () => {
    const newConsent = {
      timestamp: new Date().toISOString(),
      version: '1.0',
      cookies: false,
      analytics: false,
      marketing: false,
      preferences: false
    }
    
    localStorage.setItem('gdpr_consent', JSON.stringify(newConsent))
    setConsent(newConsent)
    setShowBanner(false)
  }

  const handleCustomize = () => {
    setShowDetails(true)
  }

  const handleCustomConsent = (customConsent) => {
    localStorage.setItem('gdpr_consent', JSON.stringify(customConsent))
    setConsent(customConsent)
    setShowBanner(false)
    setShowDetails(false)
  }

  if (!showBanner) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4"
      >
        <GlassCard className="max-w-6xl mx-auto p-6 shadow-2xl">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Shield className="w-8 h-8 text-primary-600" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-black mb-2">
                Protection de vos données personnelles
              </h3>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Nous utilisons des cookies et collectons des données pour améliorer votre expérience 
                et assurer le bon fonctionnement de nos services d'accompagnement. 
                Vos données sont protégées conformément au RGPD.
              </p>
              
              {showDetails && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mb-4 p-4 bg-gray-50 rounded-lg border"
                >
                  <h4 className="font-semibold mb-3 text-black">Détails de la collecte de données :</h4>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <h5 className="font-medium text-black">Cookies essentiels</h5>
                        <p className="text-sm text-gray-600">
                          Nécessaires au fonctionnement du site (authentification, préférences)
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Cookie className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h5 className="font-medium text-black">Données analytiques</h5>
                        <p className="text-sm text-gray-600">
                          Statistiques anonymes d'utilisation pour améliorer nos services
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Settings className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <h5 className="font-medium text-black">Préférences utilisateur</h5>
                        <p className="text-sm text-gray-600">
                          Sauvegarde de vos préférences (langue, thème, paramètres)
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <h5 className="font-medium text-blue-900 mb-2">Vos droits :</h5>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Droit d'accès à vos données</li>
                      <li>• Droit de rectification</li>
                      <li>• Droit à l'effacement</li>
                      <li>• Droit à la portabilité</li>
                      <li>• Droit d'opposition</li>
                    </ul>
                  </div>
                </motion.div>
              )}

              <div className="flex flex-wrap gap-3">
                <Button
                  variant="primary"
                  onClick={handleAcceptAll}
                  icon={CheckCircle}
                  size="sm"
                >
                  Accepter tout
                </Button>
                
                <Button
                  variant="secondary"
                  onClick={handleAcceptEssential}
                  size="sm"
                >
                  Essentiel uniquement
                </Button>
                
                <Button
                  variant="secondary"
                  onClick={handleCustomize}
                  icon={Settings}
                  size="sm"
                >
                  Personnaliser
                </Button>
                
                <button
                  onClick={handleDecline}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Refuser
                </button>
                
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  <span>{showDetails ? 'Masquer' : 'Détails'}</span>
                </button>
              </div>

              <div className="mt-3 text-xs text-gray-500">
                En continuant à utiliser ce site, vous acceptez notre{' '}
                <Link to="/privacy" className="text-primary-600 hover:underline">
                  politique de confidentialité
                </Link>{' '}
                et nos{' '}
                <Link to="/terms" className="text-primary-600 hover:underline">
                  conditions d'utilisation
                </Link>
                .
              </div>
            </div>
            
            <button
              onClick={handleDecline}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </GlassCard>
      </motion.div>
    </AnimatePresence>
  )
}

export default GDPRBanner
